import { useState } from "react";
import { Eye, Trash2, Download, FileText } from "lucide-react";
import { toast } from "sonner";
import { useJobApplications, useDeleteJobApplication } from "./queries";
import type { JobApplicationRow } from "../../api/endpoints";
import { showDeleteConfirm } from "../../components/Common/DeleteConfirm";
import { QueryError } from "../../components/Common/QueryError";
import { Pagination } from "../../components/Common/Pagination";
import { errorMessage } from "@/lib/error-message";
import { usePagination } from "@/lib/use-pagination";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { protectedFileClick } from "@/lib/protected-file";

function formatDate(value: string): string {
  try {
    return new Date(value).toLocaleString("tr-TR", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return value;
  }
}

export default function JobApplicationsList() {
  const { data, isLoading, isError, error, refetch } = useJobApplications();
  const { pageItems, page, totalPages, totalItems, setPage } = usePagination(data ?? [], 20);
  const del = useDeleteJobApplication();
  const [selected, setSelected] = useState<JobApplicationRow | null>(null);

  function handleDelete(id: string) {
    showDeleteConfirm({
      title: "Başvuruyu sil",
      description: "Bu iş başvurusu kalıcı olarak silinecek. Emin misiniz?",
      onConfirm: async () => {
        try {
          await del.mutateAsync(id);
          toast.success("Başvuru silindi.");
        } catch (e) {
          toast.error(errorMessage(e, "Silinemedi."));
        }
      },
    });
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">İş Başvuruları</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Kariyer formundan gelen başvurular ve CV'leri.
        </p>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      ) : isError && !data ? (
        <QueryError error={error} onRetry={() => void refetch()} />
      ) : !data || data.length === 0 ? (
        <p className="text-muted-foreground">Henüz başvuru yok.</p>
      ) : (
        <>
          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Aday</TableHead>
                  <TableHead>Pozisyon</TableHead>
                  <TableHead>E-posta</TableHead>
                  <TableHead>Telefon</TableHead>
                  <TableHead>Tarih</TableHead>
                  <TableHead>CV</TableHead>
                  <TableHead className="text-right">İşlem</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pageItems.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="font-medium">{row.name}</TableCell>
                    <TableCell>{row.jobTitle || "-"}</TableCell>
                    <TableCell>{row.email}</TableCell>
                    <TableCell>{row.phone}</TableCell>
                    <TableCell>{formatDate(row.createdAt)}</TableCell>
                    <TableCell>
                      {row.cvUrl ? (
                        <a
                          href={row.cvUrl}
                          onClick={protectedFileClick(row.cvUrl)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-primary hover:underline"
                        >
                          <Download className="w-4 h-4" /> İndir
                        </a>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => setSelected(row)}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(row.id)}
                          className="text-destructive"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <Pagination
            page={page}
            totalPages={totalPages}
            totalItems={totalItems}
            onPageChange={setPage}
          />
        </>
      )}

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Başvuru Detayı</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-3 gap-2">
                <span className="text-muted-foreground">Aday</span>
                <span className="col-span-2 font-medium">{selected.name}</span>
                <span className="text-muted-foreground">Pozisyon</span>
                <span className="col-span-2">{selected.jobTitle || "-"}</span>
                <span className="text-muted-foreground">E-posta</span>
                <span className="col-span-2">{selected.email}</span>
                <span className="text-muted-foreground">Telefon</span>
                <span className="col-span-2">{selected.phone}</span>
              </div>
              <div>
                <p className="text-muted-foreground mb-1">Ön Yazı</p>
                <p className="whitespace-pre-wrap rounded-md bg-muted/40 p-3">
                  {selected.coverLetter || "-"}
                </p>
              </div>
              {selected.cvUrl && (
                <a
                  href={selected.cvUrl}
                  onClick={protectedFileClick(selected.cvUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-primary hover:underline"
                >
                  <FileText className="w-4 h-4" /> CV'yi indir
                </a>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
